import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    AppstoreOutlined, TableOutlined, BankOutlined, SearchOutlined, RightOutlined,
    DollarOutlined, WalletOutlined, ReloadOutlined, FileExcelOutlined, FilePdfOutlined,
    QuestionCircleOutlined, PercentageOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { loadEntete, loadLogos, drawEtatHeaderFooter, finalizeEtatPdf, NAVY } from "../sinistre/etatsSinistresFunctions";
import {
    Button, Card, Col, ConfigProvider, DatePicker, Empty, Input, Modal, Progress,
    Row, Segmented, Select, Spin, Table, Tabs, Tag, Tour,
} from "antd";
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import SideBar from "../../../components/SideBar";
import NavBar from "../../../components/NavBar";
import Footer from "../../../components/Footer";
import axiosInstance from "../../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../../variables/constants";
import { displayNumbers } from "../../../variables/numbersFunctions";
import { peutModifierRetenueFiscale } from "../../../services/rightsService";
import RetenueFiscaleModal from "../../membres/retenue/RetenueFiscaleModal";

const { RangePicker } = DatePicker;
const PRIMARY = "#242166";

const fcfa = (v) => `${displayNumbers(Math.trunc(v || 0))} F`;
// Format compact pour les gros totaux (évite le débordement des cartes récap).
const compactFcfa = (v) => {
    const n = Math.trunc(v || 0);
    const a = Math.abs(n);
    if (a >= 1e9) return `${(n / 1e9).toFixed(2).replace(".", ",")} Md F`;
    if (a >= 1e6) return `${(n / 1e6).toFixed(1).replace(".", ",")} M F`;
    return fcfa(n);
};
const pct = (num, den) => (den > 0 ? Number(((num * 100) / den).toFixed(1)) : 0);
const strokeFor = (p) => (p >= 80 ? "#16a34a" : p >= 50 ? "#f59e0b" : "#dc2626");

// Les 6 listings du drill-down (modal). `field` = champ porteur du montant à totaliser/afficher.
const LISTING_TABS = [
    // « CA » : les lignes (contrats) produites sur la période. Libellé métier « Production »
    // (la clé CA reste inchangée : c'est le contrat d'API de /listing?type=).
    { key: "CA", label: "Production", field: "montant", color: "#242166" },
    { key: "ENCAISSEES", label: "Primes encaissées", field: "montant", color: "#16a34a" },
    { key: "NON_ENCAISSEES", label: "Primes non encaissées", field: "reste", color: "#d97706" },
    { key: "REVERSEES", label: "Primes reversées", field: "montant", color: "#16a34a" },
    { key: "NON_REVERSEES", label: "Primes non reversées", field: "montant", color: "#dc2626" },
    { key: "COMM_DUES", label: "Commissions dues", field: "commission", color: "#d97706" },
    // Libellé métier « payées » (la clé COMM_ACQUISES reste inchangée : c'est le contrat
    // d'API attendu par /api/agence/{code}/listing?type=).
    { key: "COMM_ACQUISES", label: "Commissions payées", field: "commission", color: "#16a34a" },
];

/**
 * Dashboard « Suivi agence » : CA, commissions (générées/payées/dues) et primes
 * reversées vs non reversées, par agence, sur une période. Drill-down → détail agence.
 */
const SuiviAgences = ({
    apiPath = "/api/agence/suivi",
    refreshPath = "/api/agence/suivi/refresh",
    pageTitle = "Suivi agence",
    pageSubtitle = "CA, commissions et reversements par agence",
    entityLabel = "agence",
    disableDrill = false,   // coupe TOUT le drill-down (page détail ET modales de listing)
    manageLink = null,      // lien « Gérer » dans l'en-tête (ex. /intermediaires)
    intermediaireMode = false, // suivi INTERMÉDIAIRES : le rattachement des contrats se fait par
                            // intermediaire_code_id → endpoint de listing dédié, et le détail
                            // pointe vers /intermediaire/:codeIntermediaire. La clé est `codeId`
                            // (le code_intermediaire n'est unique qu'au sein d'une compagnie).
    hideReversement = false, // masque tout le reversement → n'affiche que les commissions (dues/payées/reste)
    courtierMode = false,   // suivi COURTIERS : le scope compagnie du drill-down vient du sélecteur
                            // (ou du backend pour une compagnie), PAS de la carte — la « compagnie »
                            // d'une carte courtier est celle de ses contrats, pas un périmètre.
    autoTour = true,        // false → le tutoriel ne s'ouvre PLUS automatiquement au 1er affichage ;
                            // il reste accessible via le bouton « Tutoriel ».
} = {}) => {
    const navigate = useNavigate();
    const isPool = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";
    // Taux de retenue fiscale de la nature suivie par la page (administrateur du siège).
    const natureRetenue = courtierMode ? "COURTIER" : intermediaireMode ? "INTERMEDIAIRE" : "AGENCE";
    const [retenueModalOpen, setRetenueModalOpen] = useState(false);

    // Libellés dérivés de l'entité suivie : ce composant sert « Suivi agence », « Suivi
    // courtier » et « Suivi intermédiaire ». Sans cela la carte KPI et les exports
    // annonçaient « Agences » / « SUIVI AGENCE » y compris sur le tableau de bord courtiers.
    const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
    const entityPlural = `${entityLabel}s`;          // agences / courtiers / intermédiaires
    const entityTitle = cap(entityLabel);            // Agence  / Courtier / Intermédiaire
    const entityTitlePlural = cap(entityPlural);     // Agences / Courtiers / Intermédiaires

    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [compagnies, setCompagnies] = useState([]);
    const [codeOrganisme, setCodeOrganisme] = useState(null);
    // Période par défaut : mois en cours (1er → aujourd'hui).
    const [periode, setPeriode] = useState([dayjs().startOf("month"), dayjs()]);
    const [search, setSearch] = useState("");
    const [viewMode, setViewMode] = useState("cards");
    const [sortBy, setSortBy] = useState("ca");
    const [refreshing, setRefreshing] = useState(false);
    // Export Excel détaillé : long (un appel de listing par agence × rubrique) → on montre
    // l'avancement plutôt qu'un bouton figé pendant une minute.
    const [exportExcelLoading, setExportExcelLoading] = useState(false);
    const [exportProgress, setExportProgress] = useState(0);
    const [reloadTick, setReloadTick] = useState(0);

    // ── Tour guidé (là où le drill-down existe) : explique qu'après les filtres on
    //    clique sur une carte pour ouvrir le détail. Vu une fois → mémorisé par entité.
    const filtersRef = useRef(null);
    const cardsRef = useRef(null);
    const tourBtnRef = useRef(null);
    const TOUR_KEY = `suiviTourSeen:${entityLabel}`;
    // autoTour=false : jamais d'ouverture automatique — le tutoriel n'apparaît que sur clic du bouton.
    const [openTour, setOpenTour] = useState(
        () => autoTour && !disableDrill && localStorage.getItem(TOUR_KEY) !== "1"
    );
    const closeTour = () => { setOpenTour(false); localStorage.setItem(TOUR_KEY, "1"); };

    useEffect(() => {
        if (isPool) {
            axiosInstance.get(`/api/organisme/assurance`).then((r) => setCompagnies(r.data || [])).catch((e) => console.log(e));
        }
    }, [isPool]);

    useEffect(() => {
        setLoading(true);
        const params = {};
        if (codeOrganisme) params.codeOrganisme = codeOrganisme;
        if (periode?.length === 2) {
            params.start = periode[0].format("YYYY-MM-DD");
            params.end = periode[1].format("YYYY-MM-DD");
        }
        axiosInstance.get(apiPath, { params })
            .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
            .catch((e) => console.log(e))
            .finally(() => setLoading(false));
    }, [codeOrganisme, periode, reloadTick, apiPath]);

    // Recalcule les vues matérialisées côté serveur, puis recharge la liste.
    const handleRefresh = () => {
        setRefreshing(true);
        axiosInstance.post(refreshPath)
            .then(() => setReloadTick((t) => t + 1))
            .catch((e) => console.log(e))
            .finally(() => setRefreshing(false));
    };

    const agences = useMemo(() => {
        const list = rows.map((a) => {
            const reversees = a.primesReversees || 0;
            const nonReversees = a.primesNonReversees || 0;
            const encaissees = a.primesEncaissees != null ? a.primesEncaissees : (reversees + nonReversees);
            const ca = a.chiffreAffaires || 0;
            return {
                ...a,
                primesEncaissees: encaissees,
                tauxReversement: pct(reversees, encaissees),
                tauxEncaissement: pct(encaissees, ca),
                resteAEncaisser: Math.max(ca - encaissees, 0),
            };
        });
        const filtered = search.trim()
            ? list.filter((a) => `${a.nom || ""} ${a.codeAgence || ""} ${a.ville || ""} ${a.compagnie || ""}`.toLowerCase().includes(search.trim().toLowerCase()))
            : list;
        return [...filtered].sort((a, b) => {
            if (sortBy === "commissions") return hideReversement
                ? (b.commissionsGenerees || 0) - (a.commissionsGenerees || 0)
                : (b.commissionsDues || 0) - (a.commissionsDues || 0);
            if (sortBy === "nonReversees") return (b.primesNonReversees || 0) - (a.primesNonReversees || 0);
            if (sortBy === "contrats") return (b.nbContrats || 0) - (a.nbContrats || 0);
            if (sortBy === "name") return (a.nom || "").localeCompare(b.nom || "");
            return (b.chiffreAffaires || 0) - (a.chiffreAffaires || 0); // ca
        });
    }, [rows, search, sortBy, hideReversement]);

    const totals = useMemo(() => agences.reduce((acc, a) => ({
        ca: acc.ca + (a.chiffreAffaires || 0),
        contrats: acc.contrats + (a.nbContrats || 0),
        commGen: acc.commGen + (a.commissionsGenerees || 0),
        commPay: acc.commPay + (a.commissionsPayees || 0),
        commDues: acc.commDues + (a.commissionsDues || 0),
        encaissees: acc.encaissees + (a.primesEncaissees || 0),
        reversees: acc.reversees + (a.primesReversees || 0),
        nonReversees: acc.nonReversees + (a.primesNonReversees || 0),
    }), { ca: 0, contrats: 0, commGen: 0, commPay: 0, commDues: 0, encaissees: 0, reversees: 0, nonReversees: 0 }), [agences]);

    const fileStamp = () => new Date().toISOString().slice(0, 10);
    const trunc = (v) => Math.trunc(v || 0);

    // ── Drill-down « listings » (modal à 6 onglets) ───────────────────────────────
    const [listingAgence, setListingAgence] = useState(null);
    const [listingTab, setListingTab] = useState("ENCAISSEES");
    const [listingData, setListingData] = useState([]);
    const [listingLoading, setListingLoading] = useState(false);

    // Ouvre le modal sur l'onglet voulu (clic carte ou KPI).
    const openListing = (a, tab = "ENCAISSEES") => { setListingTab(tab); setListingAgence(a); };

    // Détail : TOUJOURS le modal de listing — plus aucune redirection vers une fiche
    // (agence / courtier / intermédiaire), en mode cartes comme en mode tableau.
    const drillTo = (a) => openListing(a, "ENCAISSEES");

    useEffect(() => {
        if (!listingAgence) return;
        setListingLoading(true);
        const params = { type: listingTab };
        const org = courtierMode ? codeOrganisme : (listingAgence.codeOrganisme || codeOrganisme);
        if (org) params.codeOrganisme = org;
        if (periode?.length === 2) {
            params.start = periode[0].format("YYYY-MM-DD");
            params.end = periode[1].format("YYYY-MM-DD");
        }
        // Intermédiaire : endpoint dédié (rattachement par intermediaire_code_id) et clé =
        // codeId, le code_intermediaire n'étant unique qu'au sein d'une compagnie.
        const listingUrl = intermediaireMode
            ? `/api/agence/intermediaire/${listingAgence.codeId}/listing`
            : `/api/agence/${listingAgence.codeAgence}/listing`;
        axiosInstance.get(listingUrl, { params })
            // __rowKey = clé stable et globalement unique : sans elle, la rowKey basée sur l'index
            // d'Ant Design (relatif à la page) collisionne entre pages → la pagination ne navigue plus.
            .then((r) => setListingData(Array.isArray(r.data) ? r.data.map((row, i) => ({ ...row, __rowKey: i })) : []))
            .catch((e) => { console.log(e); setListingData([]); })
            .finally(() => setListingLoading(false));
    }, [listingAgence, listingTab, codeOrganisme, periode]);

    // Onglets du listing : sans reversement (intermédiaires), les onglets « reversées » /
    // « non reversées » n'ont pas d'objet → on les retire, comme les KPI correspondants.
    const listingTabs = useMemo(
        () => (hideReversement
            ? LISTING_TABS.filter((t) => t.key !== "REVERSEES" && t.key !== "NON_REVERSEES")
            : LISTING_TABS),
        [hideReversement]
    );
    const listingTabDef = listingTabs.find((t) => t.key === listingTab) || listingTabs[0];
    // Total = somme des montants TELS QU'AFFICHÉS (fcfa tronque au franc), et non des valeurs
    // brutes : sinon deux commissions de 30,5 F s'affichent « 30 » et « 30 » pour un total de
    // « 61 » — l'utilisateur ne retrouve pas son addition.
    const listingTotal = useMemo(
        () => listingData.reduce((s, r) => s + Math.trunc(Number(r[listingTabDef.field]) || 0), 0),
        [listingData, listingTabDef.field]
    );

    const listingColumns = useMemo(() => {
        const base = [
            { title: "N° Police", dataIndex: "numeroPolice", key: "np", render: (v) => v || "—" },
            { title: "Souscripteur", dataIndex: "souscripteur", key: "s", render: (v) => v || "—" },
            { title: "Catégorie", dataIndex: "categorie", key: "c", render: (v) => v || "—" },
            { title: "Av.", dataIndex: "avenant", key: "av", align: "center", render: (v) => v || "—" },
            { title: "Date", dataIndex: "date", key: "d", render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "—") },
        ];
        if (listingTab === "NON_ENCAISSEES") {
            base.push({ title: "Prime", dataIndex: "montant", key: "m", align: "right", render: (v) => fcfa(v) });
            base.push({ title: "Reste à encaisser", dataIndex: "reste", key: "r", align: "right", render: (v) => <span style={{ color: "#d97706" }}>{fcfa(v)}</span> });
        } else if (listingTab === "COMM_DUES" || listingTab === "COMM_ACQUISES") {
            base.push({ title: "Commission", dataIndex: "commission", key: "cm", align: "right", render: (v) => <strong style={{ color: listingTabDef.color }}>{fcfa(v)}</strong> });
        } else if (listingTab === "CA" || listingTab === "ENCAISSEES") {
            // Ventilation plutôt qu'un total opaque : PTTC = PN compagnie + PN pool + CP + taxes.
            base.push(
                { title: "PN Compagnie", dataIndex: "primeNetteCompagnie", key: "pnc", align: "right", render: (v) => fcfa(v) },
                { title: "PN Pool", dataIndex: "primeNettePool", key: "pnp", align: "right", render: (v) => fcfa(v) },
                { title: "CP", dataIndex: "accessoires", key: "cp", align: "right", render: (v) => fcfa(v) },
                { title: "Taxes", dataIndex: "taxes", key: "tx", align: "right", render: (v) => fcfa(v) },
                { title: "PTTC", dataIndex: "primeTotale", key: "pttc", align: "right", render: (v) => <strong style={{ color: listingTabDef.color }}>{fcfa(v)}</strong> },
            );
        } else {
            base.push({ title: "Montant", dataIndex: "montant", key: "m", align: "right", render: (v) => <strong style={{ color: listingTabDef.color }}>{fcfa(v)}</strong> });
        }
        return base;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listingTab]);

    const exportListingExcel = () => {
        const data = listingData.map((r) => ({
            "N° Police": r.numeroPolice || "",
            "Souscripteur": r.souscripteur || "",
            "Catégorie": r.categorie || "",
            "Avenant": r.avenant || "",
            "Date": r.date ? dayjs(r.date).format("DD/MM/YYYY") : "",
            // Ventilation sur CA / encaissées ; sinon montant simple (même logique que les colonnes).
            ...((listingTab === "CA" || listingTab === "ENCAISSEES")
                ? {
                    "PN Compagnie": trunc(r.primeNetteCompagnie),
                    "PN Pool": trunc(r.primeNettePool),
                    "CP": trunc(r.accessoires),
                    "Taxes": trunc(r.taxes),
                    "PTTC": trunc(r.primeTotale),
                }
                : { "Montant": trunc(r.montant) }),
            ...(listingTab === "NON_ENCAISSEES" ? { "Reste à encaisser": trunc(r.reste) } : {}),
            ...((listingTab === "COMM_DUES" || listingTab === "COMM_ACQUISES") ? { "Commission": trunc(r.commission) } : {}),
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Listing");
        const name = (listingAgence?.nom || listingAgence?.codeAgence || "agence").replace(/[^\w-]+/g, "_");
        XLSX.writeFile(wb, `${name}-${listingTab.toLowerCase()}-${fileStamp()}.xlsx`);
    };

    // Export PDF du listing du modal — reprend l'entête/pied PoolIns (drawEtatHeaderFooter),
    // identique à l'export PDF de la page.
    const exportListingPDF = async () => {
        if (!listingData.length) return;
        const doc = new jsPDF({ orientation: "portrait" });
        let enteteData = null, logos = [];
        try { [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]); } catch (e) { /* optionnels */ }
        const formattedNow = new Date().toLocaleString("fr-FR", {
            day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
        const isNonEnc = listingTab === "NON_ENCAISSEES";
        const isComm = listingTab === "COMM_DUES" || listingTab === "COMM_ACQUISES";
        // CA / encaissées : ventilation PN compagnie · PN pool · CP · taxes · PTTC.
        const isVentile = listingTab === "CA" || listingTab === "ENCAISSEES";
        const head = [["N° Police", "Souscripteur", "Catégorie", "Av.", "Date",
            ...(isVentile ? ["PN Comp.", "PN Pool", "CP", "Taxes", "PTTC"]
                : isNonEnc ? ["Prime", "Reste à enc."]
                    : isComm ? ["Commission"] : ["Montant"])]];
        const body = listingData.map((r) => [
            r.numeroPolice || "", r.souscripteur || "", r.categorie || "", r.avenant || "",
            r.date ? dayjs(r.date).format("DD/MM/YYYY") : "",
            ...(isVentile
                ? [pdfNum(r.primeNetteCompagnie), pdfNum(r.primeNettePool), pdfNum(r.accessoires),
                    pdfNum(r.taxes), pdfNum(r.primeTotale)]
                : isNonEnc ? [pdfNum(r.montant), pdfNum(r.reste)]
                    : isComm ? [pdfNum(r.commission)] : [pdfNum(r.montant)]),
        ]);
        const nCols = 5 + (isVentile ? 5 : isNonEnc ? 2 : 1);
        const foot = [[
            { content: "TOTAL", colSpan: nCols - 1, styles: { halign: "right", fontStyle: "bold" } },
            { content: pdfNum(listingTotal), styles: { halign: "right", fontStyle: "bold" } },
        ]];
        const periodeLabel = periode?.length === 2
            ? ` (${periode[0].format("DD/MM/YYYY")} au ${periode[1].format("DD/MM/YYYY")})` : "";
        autoTable(doc, {
            startY: 34,
            margin: { top: 32, bottom: 20, left: 14, right: 14 },
            head, body, foot,
            styles: { fontSize: 8, cellPadding: 1.8 },
            headStyles: { fillColor: NAVY, fontSize: 8 },
            footStyles: { fillColor: [243, 244, 246], textColor: 20 },
            // Toutes les colonnes de montants (à partir de la 6ᵉ) alignées à droite.
            columnStyles: Object.fromEntries(
                Array.from({ length: nCols - 5 }, (_, i) => [5 + i, { halign: "right" }])
            ),
            didDrawPage: () => drawEtatHeaderFooter(doc, {
                title: `${(listingAgence?.nom || listingAgence?.codeAgence || "").toUpperCase()} — ${listingTabDef.label.toUpperCase()}`,
                subtitle: `${listingData.length} ligne(s) · Total ${pdfNum(listingTotal)} F${periodeLabel}`,
                enteteData, logos, formattedNow,
            }),
        });
        finalizeEtatPdf(doc);
        const name = (listingAgence?.nom || listingAgence?.codeAgence || "listing").replace(/[^\w-]+/g, "_");
        doc.save(`${name}-${listingTab.toLowerCase()}-${fileStamp()}.pdf`);
    };

    // ── Export Excel détaillé : helpers ────────────────────────────────────────────
    // Colonnes d'une rubrique — MÊME ventilation que le modal de listing et son export,
    // pour qu'un chiffre lu à l'écran se retrouve à l'identique dans le classeur.
    const colonnesRubrique = (tab) => {
        const base = ["N° Police", "Souscripteur", "Catégorie", "Avenant", "Date"];
        if (tab === "CA" || tab === "ENCAISSEES") return [...base, "PN Compagnie", "PN Pool", "CP", "Taxes", "PTTC"];
        if (tab === "NON_ENCAISSEES") return [...base, "Prime", "Reste à encaisser"];
        if (tab === "COMM_DUES" || tab === "COMM_ACQUISES") return [...base, "Commission"];
        return [...base, "Montant"];
    };
    const valeursRubrique = (tab, r) => {
        const base = [
            r.numeroPolice || "", r.souscripteur || "", r.categorie || "", r.avenant || "",
            r.date ? dayjs(r.date).format("DD/MM/YYYY") : "",
        ];
        if (tab === "CA" || tab === "ENCAISSEES") {
            return [...base, trunc(r.primeNetteCompagnie), trunc(r.primeNettePool),
                trunc(r.accessoires), trunc(r.taxes), trunc(r.primeTotale)];
        }
        if (tab === "NON_ENCAISSEES") return [...base, trunc(r.montant), trunc(r.reste)];
        if (tab === "COMM_DUES" || tab === "COMM_ACQUISES") return [...base, trunc(r.commission)];
        return [...base, trunc(r.montant)];
    };

    /** Récupère le listing d'une rubrique pour une agence (mêmes paramètres que le modal). */
    const chargerListing = async (a, tab) => {
        const params = { type: tab };
        const org = courtierMode ? codeOrganisme : (a.codeOrganisme || codeOrganisme);
        if (org) params.codeOrganisme = org;
        if (periode?.length === 2) {
            params.start = periode[0].format("YYYY-MM-DD");
            params.end = periode[1].format("YYYY-MM-DD");
        }
        const url = intermediaireMode
            ? `/api/agence/intermediaire/${a.codeId}/listing`
            : `/api/agence/${a.codeAgence}/listing`;
        try {
            const r = await axiosInstance.get(url, { params });
            return Array.isArray(r.data) ? r.data : [];
        } catch (e) {
            // Une rubrique en erreur ne doit pas faire échouer tout le classeur : on la
            // laisse vide et la feuille le signale explicitement.
            console.log(e);
            return null;
        }
    };

    // Nom de feuille Excel : 31 caractères max, et []:*?/\ interdits. On préfixe par le
    // code agence (unique) pour éviter les collisions entre noms tronqués.
    const nomFeuille = (a, prises) => {
        const brut = `${a.codeAgence || ""} ${a.nom || ""}`.trim() || "Agence";
        let nom = brut.replace(/[\[\]:*?/\\]/g, " ").replace(/\s+/g, " ").slice(0, 31).trim();
        let i = 2;
        while (prises.has(nom)) {
            const suffixe = `~${i++}`;
            nom = `${nom.slice(0, 31 - suffixe.length)}${suffixe}`;
        }
        prises.add(nom);
        return nom;
    };

    const exportExcel = () => {
        const rows = agences.map((a) => ({
            [entityTitle]: a.nom || a.codeAgence || "",
            Code: a.codeAgence || "",
            Compagnie: a.compagnie || "",
            Contrats: a.nbContrats || 0,
            "Production": trunc(a.chiffreAffaires),
            "Encaissées": trunc(a.primesEncaissees),
            "Reste à encaisser": trunc(a.resteAEncaisser),
            "Taux encaiss. (%)": a.tauxEncaissement,
            ...(hideReversement ? {
                "Comm. dues": trunc(a.commissionsGenerees),
                "Comm. payées": trunc(a.commissionsPayees),
                "Comm. reste": trunc(a.commissionsDues),
            } : {
                "Comm. dues": trunc(a.commissionsDues),
                "Reversées": trunc(a.primesReversees),
                "Non reversées": trunc(a.primesNonReversees),
                "Taux revers. (%)": a.tauxReversement,
            }),
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, `Suivi ${entityPlural}`);
        XLSX.writeFile(wb, `suivi-${entityPlural}-${fileStamp()}.xlsx`);
    };

    /**
     * Export Excel COMPLET : la feuille de synthèse (identique à `exportExcel`) suivie
     * d'UNE FEUILLE PAR AGENCE, où les rubriques du drill-down sont empilées les unes sous
     * les autres (CA, encaissées, non encaissées, reversées, non reversées, commissions…).
     *
     * Une feuille par agence plutôt qu'une par couple agence × rubrique : à 38 agences et
     * 7 rubriques, la seconde option produirait ~266 onglets — illisible et lourd à ouvrir.
     *
     * Coût : un appel de listing par agence et par rubrique. On borne la concurrence aux
     * rubriques d'une même agence et on avance agence par agence, pour ne pas saturer le
     * serveur d'un coup.
     */
    const exportExcelDetaille = async () => {
        if (!agences.length || exportExcelLoading) return;
        setExportExcelLoading(true);
        setExportProgress(0);
        try {
            const wb = XLSX.utils.book_new();

            // ── Feuille 1 : la synthèse, telle qu'affichée ──
            const synthese = agences.map((a) => ({
                [entityTitle]: a.nom || a.codeAgence || "",
                Code: a.codeAgence || "",
                Compagnie: a.compagnie || "",
                Contrats: a.nbContrats || 0,
                "Production": trunc(a.chiffreAffaires),
                "Encaissées": trunc(a.primesEncaissees),
                "Reste à encaisser": trunc(a.resteAEncaisser),
                "Taux encaiss. (%)": a.tauxEncaissement,
                ...(hideReversement ? {
                    "Comm. dues": trunc(a.commissionsGenerees),
                    "Comm. payées": trunc(a.commissionsPayees),
                    "Comm. reste": trunc(a.commissionsDues),
                } : {
                    "Comm. dues": trunc(a.commissionsDues),
                    "Reversées": trunc(a.primesReversees),
                    "Non reversées": trunc(a.primesNonReversees),
                    "Taux revers. (%)": a.tauxReversement,
                }),
            }));
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(synthese),
                `Synthèse ${entityPlural}`.slice(0, 31));

            // ── Feuilles suivantes : une par agence ──
            const prises = new Set([`Synthèse ${entityPlural}`.slice(0, 31)]);
            const periodeTxt = periode?.length === 2
                ? `du ${periode[0].format("DD/MM/YYYY")} au ${periode[1].format("DD/MM/YYYY")}`
                : "toutes périodes";

            for (let i = 0; i < agences.length; i++) {
                const a = agences[i];
                // Les rubriques d'UNE agence en parallèle ; les agences en séquence.
                const resultats = await Promise.all(listingTabs.map((t) => chargerListing(a, t.key)));

                const aoa = [
                    [`${a.nom || a.codeAgence || ""}${a.codeAgence ? ` (${a.codeAgence})` : ""}`],
                    [a.compagnie || ""],
                    [periodeTxt],
                    [],
                ];
                listingTabs.forEach((t, idx) => {
                    const lignes = resultats[idx];
                    aoa.push([t.label.toUpperCase()]);
                    if (lignes === null) {
                        aoa.push(["(rubrique indisponible — erreur de chargement)"], []);
                        return;
                    }
                    if (!lignes.length) {
                        aoa.push(["(aucune ligne sur la période)"], []);
                        return;
                    }
                    const cols = colonnesRubrique(t.key);
                    aoa.push(cols);
                    lignes.forEach((r) => aoa.push(valeursRubrique(t.key, r)));
                    // Total de la rubrique sur SA colonne porteuse (celle du KPI correspondant),
                    // pour que la somme du détail soit comparable au chiffre de la synthèse.
                    const total = lignes.reduce((sum, r) => sum + (Number(r[t.field]) || 0), 0);
                    const ligneTotal = new Array(cols.length).fill("");
                    ligneTotal[0] = `TOTAL ${t.label} (${lignes.length} ligne(s))`;
                    ligneTotal[cols.length - 1] = Math.trunc(total);
                    aoa.push(ligneTotal, []);
                });

                XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), nomFeuille(a, prises));
                setExportProgress(Math.round(((i + 1) / agences.length) * 100));
            }

            XLSX.writeFile(wb, `suivi-${entityPlural}-detaille-${fileStamp()}.xlsx`);
        } catch (e) {
            console.log(e);
        } finally {
            setExportExcelLoading(false);
            setExportProgress(0);
        }
    };

    // Format de nombre compatible jsPDF : séparateur = ESPACE ASCII (l'espace fine
    // insécable de fr-FR est rendue « / » par jsPDF).
    const pdfNum = (v) => new Intl.NumberFormat("en-DE").format(Math.floor(v || 0)).replace(/\./g, " ");

    const exportPDF = async () => {
        const doc = new jsPDF({ orientation: "landscape" });
        let enteteData = null;
        let logos = [];
        try {
            [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);
        } catch (e) { /* entête/logos optionnels */ }
        const formattedNow = new Date().toLocaleString("fr-FR", {
            day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
        });

        autoTable(doc, {
            startY: 34,
            margin: { top: 32, bottom: 20, left: 14, right: 14 },
            head: [[entityTitle, "Code", "Compagnie", "Contrats", "Production", "Encaissées", "Reste à enc.", "Tx enc.%",
                ...(hideReversement ? ["Comm. dues", "Comm. payées", "Reste"] : ["Comm. dues", "Reversées", "Non rev.", "Tx rev.%"])]],
            body: agences.map((a) => [
                a.nom || a.codeAgence || "", a.codeAgence || "", a.compagnie || "",
                a.nbContrats || 0,
                pdfNum(a.chiffreAffaires), pdfNum(a.primesEncaissees),
                pdfNum(a.resteAEncaisser), `${a.tauxEncaissement}%`,
                ...(hideReversement
                    ? [pdfNum(a.commissionsGenerees), pdfNum(a.commissionsPayees), pdfNum(a.commissionsDues)]
                    : [pdfNum(a.commissionsDues), pdfNum(a.primesReversees), pdfNum(a.primesNonReversees), `${a.tauxReversement}%`]),
            ]),
            styles: { fontSize: 7, cellPadding: 1.5 },
            headStyles: { fillColor: NAVY, fontSize: 7 },
            columnStyles: { 0: { cellWidth: 38 } },
            didDrawPage: () => drawEtatHeaderFooter(doc, {
                title: `SUIVI ${entityLabel.toUpperCase()}${periode?.length === 2 ? ` — ${periode[0].format("DD/MM/YYYY")} au ${periode[1].format("DD/MM/YYYY")}` : ""}`,
                subtitle: `${agences.length} ${entityLabel}(s)`,
                enteteData, logos, formattedNow,
            }),
        });
        finalizeEtatPdf(doc);
        doc.save(`suivi-${entityPlural}-${fileStamp()}.pdf`);
    };

    const summaryCard = (label, value, color, icon, full) => (
        <Card size="small" bordered={false} style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }} bodyStyle={{ padding: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {icon && <span style={{ width: 34, height: 34, borderRadius: 9, background: `${color}14`, color, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{icon}</span>}
                <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
                    <div title={full || undefined} style={{ fontSize: 17, fontWeight: 700, color, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
                </div>
            </div>
        </Card>
    );

    // Mini-bloc label/valeur (gère les grands montants sans débordement, contrairement à <Tag>).
    // onClick (optionnel) → ouvre le listing correspondant (sans déclencher le clic de la carte).
    const miniStat = (label, value, color, bg, onClick) => (
        <div onClick={onClick ? (e) => { e.stopPropagation(); onClick(); } : undefined}
            style={{ background: bg, border: `1px solid ${color}33`, borderRadius: 8, padding: "6px 10px", minWidth: 0, cursor: onClick ? "pointer" : undefined }}>
            <div style={{ fontSize: 10.5, color: "#6b7280", whiteSpace: "nowrap" }}>{label}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color, lineHeight: 1.25, wordBreak: "break-word" }}>{value}</div>
        </div>
    );

    const columns = [
        {
            title: "Agence", dataIndex: "nom", key: "nom",
            render: (nom, r) => (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ width: 34, height: 34, borderRadius: 8, background: `${PRIMARY}14`, color: PRIMARY, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><BankOutlined /></span>
                    <div>
                        <div style={{ fontWeight: 600, color: "#111827" }}>{nom || r.codeAgence}</div>
                        <div style={{ fontSize: 12, color: "#9ca3af" }}>{r.ville ? `${r.ville} · ` : ""}Code {r.codeAgence}{r.compagnie ? ` · ${r.compagnie}` : ""}</div>
                    </div>
                </div>
            ),
        },
        { title: "Contrats", dataIndex: "nbContrats", key: "nbContrats", align: "center", sorter: (a, b) => a.nbContrats - b.nbContrats, render: (v) => v || 0 },
        { title: "Production", dataIndex: "chiffreAffaires", key: "ca", align: "right", sorter: (a, b) => a.chiffreAffaires - b.chiffreAffaires, render: (v) => <strong style={{ color: PRIMARY }}>{fcfa(v)}</strong> },
        { title: "Encaissées", dataIndex: "primesEncaissees", key: "enc", align: "right", sorter: (a, b) => a.primesEncaissees - b.primesEncaissees, render: (v) => <span style={{ color: "#16a34a" }}>{fcfa(v)}</span> },
        { title: "Reste à encaisser", dataIndex: "resteAEncaisser", key: "reste", align: "right", sorter: (a, b) => a.resteAEncaisser - b.resteAEncaisser, render: (v) => <span style={{ color: "#d97706" }}>{fcfa(v)}</span> },
        { title: "Taux encaiss.", dataIndex: "tauxEncaissement", key: "txEnc", align: "center", sorter: (a, b) => a.tauxEncaissement - b.tauxEncaissement, render: (p) => <Progress percent={p} size="small" strokeColor={strokeFor(p)} style={{ width: 100, margin: "0 auto" }} /> },
        ...(hideReversement ? [
            { title: "Comm. dues", dataIndex: "commissionsGenerees", key: "commGen", align: "right", sorter: (a, b) => a.commissionsGenerees - b.commissionsGenerees, render: (v) => <span style={{ color: "#d97706" }}>{fcfa(v)}</span> },
            { title: "Comm. payées", dataIndex: "commissionsPayees", key: "commPay", align: "right", sorter: (a, b) => a.commissionsPayees - b.commissionsPayees, render: (v) => <span style={{ color: "#16a34a" }}>{fcfa(v)}</span> },
            { title: "Reste", dataIndex: "commissionsDues", key: "commDues", align: "right", sorter: (a, b) => a.commissionsDues - b.commissionsDues, render: (v) => <span style={{ color: "#dc2626" }}>{fcfa(v)}</span> },
        ] : [
            { title: "Comm. dues", dataIndex: "commissionsDues", key: "commDues", align: "right", sorter: (a, b) => a.commissionsDues - b.commissionsDues, render: (v) => <span style={{ color: "#d97706" }}>{fcfa(v)}</span> },
            { title: "Reversées", dataIndex: "primesReversees", key: "rev", align: "right", render: (v) => <span style={{ color: "#16a34a" }}>{fcfa(v)}</span> },
            { title: "Non reversées", dataIndex: "primesNonReversees", key: "nonrev", align: "right", sorter: (a, b) => a.primesNonReversees - b.primesNonReversees, render: (v) => <span style={{ color: "#dc2626" }}>{fcfa(v)}</span> },
            { title: "Taux revers.", dataIndex: "tauxReversement", key: "taux", align: "center", sorter: (a, b) => a.tauxReversement - b.tauxReversement, render: (p) => <Progress percent={p} size="small" strokeColor={strokeFor(p)} style={{ width: 110, margin: "0 auto" }} /> },
        ]),
        ...(disableDrill ? [] : [{ title: "", key: "action", align: "center", render: (_, r) => <Button type="text" style={{ color: PRIMARY }} onClick={() => drillTo(r)}>Détails <RightOutlined /></Button> }]),
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"MEMBRES"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <ConfigProvider locale={frFR}>
                                {/* En-tête */}
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
                                    <div>
                                        <h4 style={{ margin: 0, color: "#111827" }}>{pageTitle}</h4>
                                        <span style={{ color: "#6b7280", fontSize: 13 }}>{pageSubtitle}</span>
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                        {manageLink && (
                                            <Button type="primary" ghost icon={<BankOutlined />} onClick={() => navigate(manageLink)}>Gérer</Button>
                                        )}
                                        {peutModifierRetenueFiscale() && (
                                            <Button icon={<PercentageOutlined />} onClick={() => setRetenueModalOpen(true)}
                                                style={{ borderColor: PRIMARY, color: PRIMARY }}>
                                                Taux de retenue fiscale
                                            </Button>
                                        )}
                                        {!disableDrill && (
                                            <Button ref={tourBtnRef} icon={<QuestionCircleOutlined />} onClick={() => setOpenTour(true)}>Tutoriel</Button>
                                        )}
                                        {/* Export rapide : la seule feuille de synthèse. */}
                                        <Button icon={<FileExcelOutlined />} onClick={exportExcel}
                                            disabled={!agences.length || exportExcelLoading}
                                            style={{ borderColor: "#059669", color: "#059669" }}>Excel</Button>
                                        {/* Export complet : synthèse + une feuille par entité. Long (un appel
                                            de listing par entité × rubrique) → bouton séparé, pour ne pas
                                            imposer l'attente à qui ne veut que la synthèse. */}
                                        <Button icon={<FileExcelOutlined />} onClick={exportExcelDetaille}
                                            loading={exportExcelLoading}
                                            disabled={!agences.length}
                                            style={{ borderColor: "#047857", color: "#047857" }}>
                                            {exportExcelLoading ? `Détail… ${exportProgress}%` : "Excel détaillé"}
                                        </Button>
                                        <Button icon={<FilePdfOutlined />} onClick={exportPDF} disabled={!agences.length}
                                            style={{ borderColor: "#dc2626", color: "#dc2626" }}>PDF</Button>
                                        {refreshPath && (
                                            <Button icon={<ReloadOutlined />} loading={refreshing} onClick={handleRefresh}>
                                                Rafraîchir
                                            </Button>
                                        )}
                                        <Segmented value={viewMode} onChange={setViewMode} options={[
                                            { value: "cards", label: <span><AppstoreOutlined /> Cartes</span> },
                                            { value: "table", label: <span><TableOutlined /> Tableau</span> },
                                        ]} />
                                    </div>
                                </div>

                                <Spin spinning={loading}>
                                    {/* Récap */}
                                    <Row gutter={[12, 12]} style={{ marginBottom: 14 }}>
                                        <Col xs={12} md={8} lg={3}>{summaryCard(entityTitlePlural, agences.length, PRIMARY, <BankOutlined />)}</Col>
                                        <Col xs={12} md={8} lg={3}>{summaryCard("Contrats", (totals.contrats || 0).toLocaleString("fr-FR"), PRIMARY)}</Col>
                                        <Col xs={12} md={8} lg={3}>{summaryCard("Production", compactFcfa(totals.ca), PRIMARY, <DollarOutlined />, fcfa(totals.ca))}</Col>
                                        <Col xs={12} md={8} lg={3}>{summaryCard(`Encaissées (${pct(totals.encaissees, totals.ca)}%)`, compactFcfa(totals.encaissees), "#16a34a", <WalletOutlined />, fcfa(totals.encaissees))}</Col>
                                        <Col xs={12} md={8} lg={3}>{summaryCard("Reste à encaisser", compactFcfa(Math.max(totals.ca - totals.encaissees, 0)), "#d97706", null, fcfa(Math.max(totals.ca - totals.encaissees, 0)))}</Col>
                                        {hideReversement ? (
                                            <>
                                                <Col xs={12} md={8} lg={3}>{summaryCard("Comm. dues", compactFcfa(totals.commGen), "#d97706", null, fcfa(totals.commGen))}</Col>
                                                <Col xs={12} md={8} lg={3}>{summaryCard("Comm. payées", compactFcfa(totals.commPay), "#16a34a", null, fcfa(totals.commPay))}</Col>
                                                <Col xs={12} md={8} lg={3}>{summaryCard("Comm. reste", compactFcfa(totals.commDues), "#dc2626", null, fcfa(totals.commDues))}</Col>
                                            </>
                                        ) : (
                                            <>
                                                <Col xs={12} md={8} lg={3}>{summaryCard("Comm. dues", compactFcfa(totals.commDues), "#d97706", null, fcfa(totals.commDues))}</Col>
                                                <Col xs={12} md={8} lg={3}>{summaryCard("Reversées", compactFcfa(totals.reversees), "#16a34a", null, fcfa(totals.reversees))}</Col>
                                                <Col xs={12} md={8} lg={3}>{summaryCard("À reverser", compactFcfa(totals.nonReversees), "#dc2626", null, fcfa(totals.nonReversees))}</Col>
                                            </>
                                        )}
                                    </Row>

                                    {/* Filtres */}
                                    <Card ref={filtersRef} bordered={false} style={{ borderRadius: 12, marginBottom: 14, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }} bodyStyle={{ padding: 12 }}>
                                        <Row gutter={[12, 12]} align="middle">
                                            <Col xs={24} md={7}>
                                                <Input allowClear prefix={<SearchOutlined style={{ color: "#9ca3af" }} />} placeholder={`Rechercher un(e) ${entityLabel}…`} value={search} onChange={(e) => setSearch(e.target.value)} />
                                            </Col>
                                            <Col xs={24} md={7}>
                                                <RangePicker style={{ width: "100%" }} value={periode} onChange={setPeriode} format="DD/MM/YYYY" placeholder={["Début", "Fin"]} />
                                            </Col>
                                            {isPool && (
                                                <Col xs={24} md={6}>
                                                    <Select showSearch allowClear style={{ width: "100%" }} placeholder="Compagnie…" optionFilterProp="label"
                                                        value={codeOrganisme || undefined} onChange={(v) => setCodeOrganisme(v || null)}
                                                        options={(compagnies || []).map((c) => ({ value: c.code, label: c.raisonSociale || c.code }))} />
                                                </Col>
                                            )}
                                            <Col xs={24} md={isPool ? 4 : 10}>
                                                <Select style={{ width: "100%" }} value={sortBy} onChange={setSortBy} options={[
                                                    { value: "ca", label: "Trier : CA" },
                                                    { value: "commissions", label: "Trier : Commissions dues" },
                                                    ...(hideReversement ? [] : [{ value: "nonReversees", label: "Trier : À reverser" }]),
                                                    { value: "contrats", label: "Trier : Contrats" },
                                                    { value: "name", label: "Trier : Nom" },
                                                ]} />
                                            </Col>
                                        </Row>
                                    </Card>

                                    {/* Contenu */}
                                    <div ref={cardsRef}>
                                    {agences.length === 0 ? (
                                        <Empty description={`Aucun(e) ${entityLabel}`} style={{ padding: "50px 0" }} />
                                    ) : viewMode === "table" ? (
                                        <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                                            <style>{`
                                                .suivi-table .ant-table-cell { white-space: nowrap; }
                                                .suivi-table .ant-table-thead .ant-table-cell { white-space: nowrap; }
                                            `}</style>
                                            <Table className="suivi-table" rowKey="codeId" columns={columns} dataSource={agences}
                                                scroll={{ x: "max-content" }}
                                                pagination={{ pageSize: 12, showSizeChanger: true }}
                                                onRow={disableDrill ? undefined : (r) => ({ onClick: () => drillTo(r), style: { cursor: "pointer" } })} />
                                        </Card>
                                    ) : (
                                        <Row gutter={[16, 16]}>
                                            {agences.map((a) => (
                                                <Col xs={24} sm={12} lg={8} xxl={6} key={a.codeId}>
                                                    <Card hoverable={!disableDrill} onClick={disableDrill ? undefined : () => openListing(a, "ENCAISSEES")} bordered={false}
                                                        style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.07)", height: "100%" }} bodyStyle={{ padding: 18 }}>
                                                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                                                            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                                                                <span style={{ width: 40, height: 40, borderRadius: 10, background: `${PRIMARY}14`, color: PRIMARY, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}><BankOutlined /></span>
                                                                <div style={{ minWidth: 0 }}>
                                                                    <div style={{ fontWeight: 600, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.nom || a.codeAgence}</div>
                                                                    <div title={`${a.ville ? `${a.ville} · ` : ""}${a.codeAgence}${a.compagnie ? ` · ${a.compagnie}` : ""}`}
                                                                        style={{ marginTop: 2, fontSize: 11, color: "#6b7280", background: "#f5f5f5", border: "1px solid #eee", borderRadius: 6, padding: "1px 8px", display: "inline-block", maxWidth: "100%", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                        {a.ville ? `${a.ville} · ` : ""}{a.codeAgence}{a.compagnie ? ` · ${a.compagnie}` : ""}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <Progress type="circle" size={52} percent={a.tauxEncaissement} strokeColor={strokeFor(a.tauxEncaissement)} format={(p) => `${p}%`} />
                                                        </div>

                                                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14, paddingTop: 12, borderTop: "1px solid #f0f0f0" }}>
                                                            <div style={{ textAlign: "center", flex: 1 }}>
                                                                <div style={{ fontSize: 11, color: "#9ca3af" }}>Contrats</div>
                                                                <div style={{ fontWeight: 700, color: PRIMARY }}>{a.nbContrats || 0}</div>
                                                            </div>
                                                            {/* CA cliquable → listing des lignes qui le composent. */}
                                                            <div onClick={disableDrill ? undefined : (e) => { e.stopPropagation(); openListing(a, "CA"); }}
                                                                style={{ textAlign: "center", flex: 1, borderLeft: "1px solid #f0f0f0", borderRight: "1px solid #f0f0f0", cursor: disableDrill ? undefined : "pointer" }}>
                                                                <div style={{ fontSize: 11, color: "#9ca3af" }}>Production</div>
                                                                <div style={{ fontWeight: 700, color: PRIMARY, fontSize: 13 }}>{fcfa(a.chiffreAffaires)}</div>
                                                            </div>
                                                            <div onClick={disableDrill ? undefined : (e) => { e.stopPropagation(); openListing(a, "ENCAISSEES"); }}
                                                                style={{ textAlign: "center", flex: 1, cursor: disableDrill ? undefined : "pointer" }}>
                                                                <div style={{ fontSize: 11, color: "#9ca3af" }}>Encaissées</div>
                                                                <div style={{ fontWeight: 700, color: "#16a34a", fontSize: 13 }}>{fcfa(a.primesEncaissees)}</div>
                                                            </div>
                                                        </div>

                                                        {/* Produit → Encaissé → Reversé (mini-blocs cliquables → listing) */}
                                                        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                                            {miniStat("Reste à encaisser", fcfa(a.resteAEncaisser), "#d97706", "#fff7e6", disableDrill ? undefined : () => openListing(a, "NON_ENCAISSEES"))}
                                                            {hideReversement ? (
                                                                <>
                                                                    {miniStat("Comm. dues", fcfa(a.commissionsGenerees), "#d97706", "#fff7e6", disableDrill ? undefined : () => openListing(a, "COMM_DUES"))}
                                                                    {miniStat("Comm. payées", fcfa(a.commissionsPayees), "#16a34a", "#f6ffed")}
                                                                    {miniStat("Reste", fcfa(a.commissionsDues), "#dc2626", "#fff1f0")}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {miniStat("Comm. dues", fcfa(a.commissionsDues), "#6b7280", "#f5f5f5", disableDrill ? undefined : () => openListing(a, "COMM_DUES"))}
                                                                    {miniStat("Reversé", fcfa(a.primesReversees), "#16a34a", "#f6ffed", disableDrill ? undefined : () => openListing(a, "REVERSEES"))}
                                                                    {miniStat("À reverser", fcfa(a.primesNonReversees), "#dc2626", "#fff1f0", disableDrill ? undefined : () => openListing(a, "NON_REVERSEES"))}
                                                                </>
                                                            )}
                                                        </div>
                                                        <div style={{ marginTop: 6, textAlign: "center", fontSize: 11, color: "#9ca3af" }}>
                                                            Encaissement {a.tauxEncaissement}%{hideReversement ? "" : ` · Reversement ${a.tauxReversement}%`}
                                                        </div>
                                                        {/* Plus de lien « Fiche détaillée » : le détail se consulte UNIQUEMENT
                                                            dans le modal de listing (clic sur la carte ou sur un KPI), pour
                                                            les agences comme pour les courtiers et intermédiaires. */}
                                                    </Card>
                                                </Col>
                                            ))}
                                        </Row>
                                    )}
                                    </div>
                                </Spin>

                                {/* Drill-down : modal à 6 onglets (clic carte / KPI) */}
                                <Modal
                                    open={!!listingAgence}
                                    onCancel={() => setListingAgence(null)}
                                    width={1040}
                                    footer={null}
                                    destroyOnClose
                                    title={listingAgence ? (
                                        <span>
                                            <BankOutlined style={{ color: PRIMARY, marginRight: 8 }} />
                                            {listingAgence.nom || listingAgence.codeAgence}
                                            <span style={{ color: "#9ca3af", fontWeight: 400, fontSize: 13 }}>
                                                {" "}— listings{periode?.length === 2 ? ` · ${periode[0].format("DD/MM/YYYY")} au ${periode[1].format("DD/MM/YYYY")}` : ""}
                                            </span>
                                        </span>
                                    ) : ""}
                                >
                                    <Tabs activeKey={listingTab} onChange={setListingTab}
                                        items={listingTabs.map((t) => ({ key: t.key, label: t.label }))} />
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                                        <span style={{ fontWeight: 700, color: listingTabDef.color }}>
                                            Total : {fcfa(listingTotal)} <span style={{ color: "#9ca3af", fontWeight: 400 }}>({listingData.length} ligne{listingData.length > 1 ? "s" : ""})</span>
                                        </span>
                                        <div style={{ display: "flex", gap: 8 }}>
                                            <Button size="small" icon={<FileExcelOutlined />} onClick={exportListingExcel}
                                                disabled={!listingData.length} style={{ borderColor: "#059669", color: "#059669" }}>Excel</Button>
                                            <Button size="small" icon={<FilePdfOutlined />} onClick={exportListingPDF}
                                                disabled={!listingData.length} style={{ borderColor: "#dc2626", color: "#dc2626" }}>PDF</Button>
                                        </div>
                                    </div>
                                    <Table size="small" rowKey="__rowKey"
                                        loading={listingLoading} columns={listingColumns} dataSource={listingData}
                                        scroll={{ x: "max-content" }}
                                        pagination={{ defaultPageSize: 8, showSizeChanger: true, pageSizeOptions: [8, 16, 32, 64], showTotal: (t) => `${t} ligne(s)` }}
                                        locale={{ emptyText: "Aucune ligne pour ce listing sur la période" }} />
                                </Modal>

                                {/* Tour guidé : après les filtres, cliquer sur une carte ouvre le détail (modal). */}
                                {!disableDrill && (
                                    <Tour
                                        open={openTour}
                                        onClose={closeTour}
                                        steps={[
                                            {
                                                title: "1. Appliquez vos filtres",
                                                description: `Filtrez par recherche, période${isPool ? ", compagnie" : ""} et tri. La liste se met à jour automatiquement.`,
                                                target: () => filtersRef.current,
                                            },
                                            {
                                                title: `2. Cliquez sur un ${entityLabel}`,
                                                description: "Après avoir appliqué vos filtres, cliquez sur une carte (ou une ligne) pour ouvrir le détail : primes encaissées / à encaisser, reversées, commissions dues… Le détail est exportable en Excel et en PDF.",
                                                target: () => cardsRef.current,
                                            },
                                            {
                                                title: "Revoir ce guide",
                                                description: "Rouvrez ce tutoriel à tout moment depuis ce bouton.",
                                                target: () => tourBtnRef.current,
                                            },
                                        ]}
                                    />
                                )}
                                {peutModifierRetenueFiscale() && (
                                    <RetenueFiscaleModal
                                        nature={natureRetenue}
                                        open={retenueModalOpen}
                                        onClose={() => setRetenueModalOpen(false)}
                                    />
                                )}
                            </ConfigProvider>
                        </div>
                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default SuiviAgences;
